import {
    defineStore,
} from 'pinia'
import { DateTime } from 'luxon'
import {
    useContextStore,
} from '~~/stores/context'
import { ValidationError } from '~~/app/composables/validation'
import { PROVIDERS } from '#shared/providers'
import { selectZoneForRecordName } from '#shared/cloudflare-zones'
import { buildCertificateValidationRecordName, buildOwnershipRecordName } from '#shared/cloudflare-custom-hostnames'
import type { CnameTargets } from '~~/server/lib/cloudflare/custom-hostnames'
import type { CnameChainStatus } from '#shared/cloudflare-custom-hostname-cname'

const PROVIDER = PROVIDERS.CLOUDFLARE

export type CloudflareDnsRecordInput = {
    type: string
    name: string
    content: string
    ttl: number
    priority?: number
    proxied: boolean
}

export type CloudflareDnsRecordReference = {
    id: string
    type: string
    name: string
}

export type CloudflareHostedDnsRecord = CloudflareDnsRecordReference & {
    zone: string
}

export type CloudflareCustomHostnameInput = {
    domainId: number | null
    subdomain: string
}

export type CloudflareTxtRecordTarget = {
    name: string
    value?: string
}

export type CloudflareDnsRecord = {
    id: string
    type: string
    name: string
    content: string
    priority?: number
    ttl: number
    proxied: boolean
    comment: string | null
    created_on: string
    modified_on: string
}

export type CloudflareDnsRecordQuery = {
    type?: string
    name: string
}

export type CloudflareDnsRecordCheck<TEvaluation extends object = object> = TEvaluation & {
    checkedOn: string
}

export type CloudflareVisitedHostname = {
    customHostname: any
    cnameTargets: CnameTargets | null
}

export type CloudflareVisitedZone = {
    dnsRecords: any[]
    customHostnames: any[]
    hostnames: Record<string, CloudflareVisitedHostname>
}

const CNAME_RECORD_TYPE = 'CNAME'
export const AUTOMATIC_TTL = 1

function buildVisitedZone(): CloudflareVisitedZone {
    return {
        dnsRecords: [],
        customHostnames: [],
        hostnames: {},
    }
}

function buildVisitedHostname(): CloudflareVisitedHostname {
    return {
        customHostname: null,
        cnameTargets: null,
    }
}

function stampCheckedOn(): string {
    return DateTime.now().toISO() as string
}

async function countRunning(
    runningChecks: Record<string, number>,
    key: string,
    run: () => Promise<void>,
): Promise<void> {
    runningChecks[key] = (runningChecks[key] ?? 0) + 1

    try {
        await run()
    }
    finally {
        runningChecks[key] -= 1
    }
}

function findVisitedHostname(
    visitedZones: Record<string, CloudflareVisitedZone>,
    zone: string,
    hostname: string,
): CloudflareVisitedHostname | undefined {
    return visitedZones[zone]?.hostnames[hostname]
}

function listOwnershipRecords(customHostname: any): CloudflareTxtRecordTarget[] {
    const ownership = customHostname?.ownership_verification
    if (!ownership?.name) {
        return [
            {
                name: buildOwnershipRecordName(customHostname?.hostname),
            },
        ]
    }

    return [
        {
            name: ownership.name,
            value: ownership.value,
        },
    ]
}

function listValidationRecords(customHostname: any): CloudflareTxtRecordTarget[] {
    const records: { txt_name?: string, txt_value: string }[] = customHostname?.ssl?.validation_records ?? []
    const txtRecords = records.filter((record) => record.txt_name)
    if (txtRecords.length === 0) {
        return [
            {
                name: buildCertificateValidationRecordName(customHostname?.hostname),
            },
        ]
    }

    return txtRecords.map((record): CloudflareTxtRecordTarget => {
        return {
            name: record.txt_name as string,
            value: record.txt_value,
        }
    })
}

function postDnsRecord(zone: string, record: CloudflareDnsRecordInput): Promise<any> {
    return $fetch(`/api/cloudflare/zones/${zone}/dns/records`, {
        method: 'POST',
        body: record,
    })
}

export const useCloudflareStore = defineStore('cloudflare', {
    state: () => {
        return {
            _initialized: false,
            zones: [],
            visitedZones: {} as Record<string, CloudflareVisitedZone>,
            dnsRecordChecks: {} as Record<string, Record<string, CloudflareDnsRecordCheck>>,
            runningDnsRecordChecks: {} as Record<string, Record<string, number>>,
            dnsRecordTypes: [
                'A',
                'AAAA',
                'CNAME',
                'HTTPS',
                'TXT',
                'SRV',
                'LOC',
                'MX',
                'NS',
                'CERT',
                'DNSKEY',
                'DS',
                'NAPTR',
                'SMIMEA',
                'SSHFP',
                'SVCB',
                'TLSA',
                'URI',
            ],
        }
    },

    getters: {
        getDnsRecords: (state) => {
            return (zone: string): any[] => {
                return state.visitedZones[zone]?.dnsRecords ?? []
            }
        },

        getCustomHostnames: (state) => {
            return (zone: string): any[] => {
                return state.visitedZones[zone]?.customHostnames ?? []
            }
        },

        getCustomHostname: (state) => {
            return (zone: string, hostname: string): any => {
                return findVisitedHostname(state.visitedZones, zone, hostname)?.customHostname ?? null
            }
        },

        getDnsRecordCheck: (state) => {
            return <TEvaluation extends object>(zone: string, key: string): CloudflareDnsRecordCheck<TEvaluation> | undefined => {
                const check = state.dnsRecordChecks[zone]?.[key]

                return check as CloudflareDnsRecordCheck<TEvaluation> | undefined
            }
        },

        isDnsRecordChecking: (state) => {
            return (zone: string, key: string): boolean => {
                const running = state.runningDnsRecordChecks[zone]?.[key] ?? 0

                return running > 0
            }
        },

        getCnameTargets: (state) => {
            return (zone: string, hostname: string): CnameTargets | null => {
                return findVisitedHostname(state.visitedZones, zone, hostname)?.cnameTargets ?? null
            }
        },

        getOwnershipRecords: (state) => {
            return (zone: string, hostname: string): CloudflareTxtRecordTarget[] => {
                const customHostname = findVisitedHostname(state.visitedZones, zone, hostname)?.customHostname

                return listOwnershipRecords(customHostname)
            }
        },

        getValidationRecords: (state) => {
            return (zone: string, hostname: string): CloudflareTxtRecordTarget[] => {
                const customHostname = findVisitedHostname(state.visitedZones, zone, hostname)?.customHostname

                return listValidationRecords(customHostname)
            }
        },

        getZoneForRecordName: (state) => {
            return (name: string): { name: string } | null => {
                const zones = state.zones as { name: string }[]

                return selectZoneForRecordName(zones, name)
            }
        },
    },

    actions: {
        init() {
            if (this._initialized) return
            this._initialized = true

            const contextStore = useContextStore()
            contextStore.registerProvider(PROVIDER)

            watch((): string | undefined => contextStore.getContext(PROVIDER)?.identityKey, (): void => {
                this.zones = []
                this.visitedZones = {}
                this.dnsRecordChecks = {}
            })
        },

        visitZone(zone: string): CloudflareVisitedZone {
            this.visitedZones[zone] ??= buildVisitedZone()

            return this.visitedZones[zone]
        },

        visitHostname(zone: string, hostname: string): CloudflareVisitedHostname {
            const visitedZone = this.visitZone(zone)
            visitedZone.hostnames[hostname] ??= buildVisitedHostname()

            return visitedZone.hostnames[hostname]
        },

        storeCustomHostname(zone: string, hostname: string, customHostname: any): void {
            const visitedHostname = this.visitHostname(zone, hostname)
            visitedHostname.customHostname = customHostname
        },

        async fetchZones() {
            await useRequest({
                label: `fetch cloudflare zones`,
                fetch: () => $fetch('/api/cloudflare/zones', {
                    method: 'GET',
                }),
                evaluate: ({ zones }) => [...zones],
                store: (zones) => this.zones = zones,
                successDetails: (zones) => zones.length,
            })
        },

        async fetchDnsRecords(zone: string): Promise<void> {
            await useRequest({
                label: `fetch cloudflare dns records ${zone}`,
                fetch: () => $fetch(`/api/cloudflare/zones/${zone}/dns/records`, {
                    method: 'GET',
                }),
                evaluate: ({ dnsRecords }) => [...dnsRecords],
                store: (dnsRecords) => this.visitZone(zone).dnsRecords = dnsRecords,
                successDetails: (dnsRecords) => dnsRecords.length,
            })
        },

        async fetchCustomHostnames(zone: string): Promise<void> {
            await useRequest({
                label: `fetch cloudflare custom hostnames ${zone}`,
                fetch: () => $fetch(`/api/cloudflare/zones/${zone}/custom-hostnames`, {
                    method: 'GET',
                }),
                evaluate: ({ customHostnames }) => [...customHostnames],
                store: (customHostnames) => this.visitZone(zone).customHostnames = customHostnames,
                successDetails: (customHostnames) => customHostnames.length,
            })
        },

        async fetchCustomHostname(zone: string, hostname: string): Promise<void> {
            await useRequest({
                label: `fetch cloudflare custom hostname ${hostname}`,
                fetch: () => $fetch(`/api/cloudflare/zones/${zone}/custom-hostnames/${encodeURIComponent(hostname)}`, {
                    method: 'GET',
                }),
                evaluate: ({ customHostname }) => customHostname,
                store: (customHostname) => this.storeCustomHostname(zone, hostname, customHostname),
            })
        },

        async revalidateCustomHostname(zone: string, hostname: string): Promise<void> {
            await useRequest({
                label: `revalidate cloudflare custom hostname ${hostname}`,
                fetch: () => $fetch(`/api/cloudflare/zones/${zone}/custom-hostnames/${encodeURIComponent(hostname)}/revalidate`, {
                    method: 'POST',
                }),
                evaluate: ({ customHostname }) => customHostname,
                store: (customHostname) => this.storeCustomHostname(zone, hostname, customHostname),
            })
        },

        async fetchCnameTargets(zone: string, hostname: string): Promise<void> {
            await useRequest({
                label: `fetch cloudflare cname targets ${hostname}`,
                fetch: () => $fetch(`/api/cloudflare/zones/${zone}/custom-hostnames/${encodeURIComponent(hostname)}/cname-targets`, {
                    method: 'GET',
                }),
                evaluate: (targets) => targets as CnameTargets,
                store: (targets) => this.visitHostname(zone, hostname).cnameTargets = targets,
            })
        },

        async fetchCnameStatus(saasZone: string, hostname: string, target: string): Promise<CnameChainStatus | null> {
            const status = await useRequest({
                label: `fetch cloudflare cname status ${hostname}`,
                fetch: () => $fetch(`/api/cloudflare/zones/${saasZone}/custom-hostnames/${encodeURIComponent(hostname)}/cname-status`, {
                    method: 'GET',
                    query: {
                        target,
                    },
                }),
                evaluate: ({ status }) => status as CnameChainStatus,
            })

            return status ?? null
        },

        async applyCnameRecord(hostedZone: string, hostname: string, target: string): Promise<boolean> {
            const dnsRecord = await useRequest({
                label: `apply cloudflare cname record ${hostname}`,
                fetch: () => postDnsRecord(hostedZone, {
                    type: CNAME_RECORD_TYPE,
                    name: hostname,
                    content: target,
                    ttl: AUTOMATIC_TTL,
                    proxied: true,
                }),
                evaluate: ({ dnsRecord }) => dnsRecord,
            })

            return dnsRecord != null
        },

        async readDnsRecords(zone: string, { type, name }: CloudflareDnsRecordQuery): Promise<CloudflareDnsRecord[] | null> {
            const query =
                type == null
                    ? { name }
                    : {
                        type,
                        name,
                    }
            const dnsRecords = await useRequest({
                label: `read cloudflare dns records ${name}`,
                fetch: () => $fetch(`/api/cloudflare/zones/${zone}/dns/records`, {
                    method: 'GET',
                    query,
                }),
                evaluate: ({ dnsRecords }) => dnsRecords as CloudflareDnsRecord[],
            })

            return dnsRecords ?? null
        },

        async trackDnsRecordCheck<TEvaluation extends object>(
            zone: string,
            key: string,
            evaluate: () => Promise<TEvaluation | null>,
        ): Promise<void> {
            this.runningDnsRecordChecks[zone] ??= {}
            const runningChecks = this.runningDnsRecordChecks[zone]

            await countRunning(runningChecks, key, async (): Promise<void> => {
                const evaluation = await evaluate()
                if (evaluation == null) {
                    return
                }

                this.dnsRecordChecks[zone] ??= {}
                this.dnsRecordChecks[zone][key] = {
                    ...evaluation,
                    checkedOn: stampCheckedOn(),
                }
            })
        },

        async deleteDnsRecord(zone: string, { id, type, name }: CloudflareDnsRecordReference): Promise<void> {
            await useRequest({
                label: `delete cloudflare dns record ${id}`,
                fetch: () => $fetch(`/api/cloudflare/zones/${zone}/dns/records/${id}`, {
                    method: 'DELETE',
                    query: {
                        type,
                        name,
                    },
                }),
                propagate: true,
            })
        },

        async deleteCustomHostname(zone: string, hostname: string, records: CloudflareHostedDnsRecord[]): Promise<void> {
            for (const { zone: recordZone, ...reference } of records) {
                await this.deleteDnsRecord(recordZone, reference)
            }

            await useRequest({
                label: `delete cloudflare custom hostname ${hostname}`,
                fetch: () => $fetch(`/api/cloudflare/zones/${zone}/custom-hostnames/${encodeURIComponent(hostname)}`, {
                    method: 'DELETE',
                }),
                propagate: true,
            })
        },

        async createCustomHostname(
            zone: string,
            { domainId, subdomain }: CloudflareCustomHostnameInput,
            propagate = false,
        ): Promise<string> {
            if (domainId == null || !subdomain) {
                throw new ValidationError('Domain and subdomain are required.')
            }

            const customHostname = await useRequest({
                label: `create cloudflare custom hostname ${zone}`,
                fetch: () => $fetch(`/api/cloudflare/zones/${zone}/custom-hostnames`, {
                    method: 'POST',
                    body: {
                        domainId,
                        subdomain,
                    },
                }),
                evaluate: ({ customHostname }) => customHostname,
                store: (customHostname) => this.visitHostname(zone, customHostname.hostname).customHostname = customHostname,
                propagate,
            })

            return customHostname?.hostname as string
        },

        async createDnsRecord(zone: string, record: CloudflareDnsRecordInput): Promise<void> {
            await useRequest({
                label: `create cloudflare dns record ${zone}`,
                fetch: () => postDnsRecord(zone, record),
                evaluate: ({ dnsRecord }) => dnsRecord,
                store: (dnsRecord) => {
                    const visitedZone = this.visitZone(zone)
                    visitedZone.dnsRecords = [
                        ...visitedZone.dnsRecords,
                        dnsRecord,
                    ]
                },
                propagate: true,
            })
        },
    },
})
